import { useEffect, useState } from "react";
import {
  Heart,
  Share2,
  MapPin,
  Clock,
  DollarSign,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MenuSection } from "./menu-section";
import { RatingsAndReviews } from "./rating-review";
import { handleGetRequest, handlePostRequest } from "../../hooks/api";
import { successToast, errorToast } from "../../utils/toasters";
import { googleMapImageHandler } from "../../services/googlemap";
import EditBusinessButton from "./EditBusinessButton";

export function BusinessContent({
  businessDetail,
  isOwner,
  businessId,
  businessOffers,
  additionalInfo,
  businessCatalog,
  handleFormSubmit,
  handleBusinessOfferSubmit,
  isLoading,
  handleBusinessCatalogSubmit,
  handleDetailsSubmit,
}) {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [userReviews, setUserReviews] = useState([]);
  const [fetcher, setFetcher] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);

  const [averageRating, setAverageRating] = useState("0:0:0");

  // Smart back navigation function
  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1 && document.referrer) {
      // Check if the referrer is from the same domain
      const referrerUrl = new URL(document.referrer);
      const currentUrl = new URL(window.location.href);

      if (referrerUrl.origin === currentUrl.origin) {
        // Go back to previous page if it's from the same site
        navigate(-1);
      } else {
        // If referrer is external, go to discover
        navigate("/discover");
      }
    } else {
      // No history or direct access, go to discover
      navigate("/discover");
    }
  };

  function calculateAverageRatingsWithDecimals(reviews) {
    if (!reviews || reviews.length === 0) {
      return "0:0:0";
    }

    const totals = reviews.reduce(
      (acc, review) => {
        acc.quality += review.quality_rating || 0;
        acc.service += review.service_rating || 0;
        acc.price += review.price_rating || 0;
        return acc;
      },
      { quality: 0, service: 0, price: 0 }
    );

    const count = reviews.length;
    const avgQuality = (totals.quality / count).toFixed(0);
    const avgService = (totals.service / count).toFixed(0);
    const avgPrice = (totals.price / count).toFixed(0);

    return `${avgQuality}:${avgService}:${avgPrice}`;
  }

  useEffect(() => {
    async function fetchReviews() {
      try {
        setIsProcessing(true);
        const payload = { google_place_id: businessDetail.place_id };
        const reviewsResponse = await handlePostRequest(
          "/business/getBusinessRating",
          payload,
          {}
        );
        if (
          reviewsResponse &&
          reviewsResponse.data &&
          reviewsResponse.data.length > 0
        ) {
          setUserReviews(reviewsResponse.data);
        }
        setAverageRating(
          calculateAverageRatingsWithDecimals(reviewsResponse.data)
        );
      } catch (error) {
        errorToast("Error while fetching reviews");
      } finally {
        setIsProcessing(false);
      }
    }
    fetchReviews();
  }, [fetcher]);

  function getClosingTimeText(openingHours) {
    if (!openingHours) {
      return;
    }
    if (!openingHours.open_now) return "CLOSED";

    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sunday) to 6 (Saturday)

    const todayPeriod = openingHours.periods.find(
      (p) => p.open.day === currentDay
    );
    if (!todayPeriod) return "Unknown hours";

    // Convert "HHMM" to "hh:mm AM/PM"
    const formatTime = (timeStr) => {
      const hour = parseInt(timeStr.substring(0, 2), 10);
      const minute = timeStr.substring(2);
      const date = new Date();
      date.setHours(hour);
      date.setMinutes(minute);
      return date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    };

    const closingTime = formatTime(todayPeriod.close.time);

    return `Closes by ${closingTime}`;
  }

  const [isFavorite, setIsFavorite] = useState(false);

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };
  const hours =
    businessDetail?.opening_hours?.open_now !== undefined
      ? {
          open_now: businessDetail.opening_hours.open_now ?? null,
          periods: businessDetail.opening_hours.periods ?? null,
          weekday_text: businessDetail.opening_hours.weekday_text ?? null,
        }
      : null;

  useEffect(() => {
    async function fetchImage() {
      if (!businessDetail.photos) {
        return;
      }

      const photoReference = businessDetail.photos[0].photo_reference;
      const imageData = await googleMapImageHandler({
        photo_reference: photoReference,
        maxwidth: 400,
      });

      setImageUrl(imageData);
    }
    fetchImage();
  }, []);

  return (
    <div className="flex-1 overflow-auto w-full">
      {/* Back Button and Edit Business Button - Left and Right aligned */}
      <div className="flex justify-between items-center p-4 sm:p-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-3 py-2 text-black hover:bg-[#f8f0ff] rounded-lg transition-colors duration-200"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back</span>
        </button>

        {isOwner && (
          <EditBusinessButton
            businessid={businessId}
            handleFormSubmit={handleFormSubmit}
            isLoading={isLoading}
            handleBusinessOfferSubmit={handleBusinessOfferSubmit}
            handleBusinessCatalogSubmit={handleBusinessCatalogSubmit}
            handleDetailsSubmit={handleDetailsSubmit}
          />
        )}
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="flex-1 overflow-y-auto">
          <div className="relative">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={businessDetail.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={"/image/chicken.png?height=400&width=800"}
                alt={businessDetail.name}
                className="w-full h-[300px] sm:h-[400px] object-cover"
              />
            )}

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 sm:p-6">
              <div className="bg-white rounded-full px-3 py-1 text-xs font-medium flex items-center w-fit mb-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-1"
                >
                  <path
                    d="M22 10C22 14.4183 17.5228 19 12 19C6.47715 19 2 14.4183 2 10C2 5.58172 6.47715 2 12 2C17.5228 2 22 5.58172 22 10Z"
                    fill="#F5F3F5"
                  />
                  <path
                    d="M15 10C15 11.6569 13.6569 13 12 13C10.3431 13 9 11.6569 9 10C9 8.34315 10.3431 7 12 7C13.6569 7 15 8.34315 15 10Z"
                    fill="#7B189F"
                  />
                  <path
                    d="M6 10H4.5M19.5 10H18M7.5 14.5L6.5 15.5M16.5 14.5L17.5 15.5M7.5 5.5L6.5 4.5M16.5 5.5L17.5 4.5"
                    stroke="#7B189F"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {businessDetail.types && businessDetail.types[0].toUpperCase()}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {businessDetail.name}
              </h1>
              <div className="flex items-center text-white">
                <div className="flex items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#FFC107"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                      stroke="#FFC107"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="ml-1 font-semibold">{averageRating}</span>
                </div>
                <div className="text-xs ml-2 opacity-80">
                  (Quality · Service · Price) • Overall {userReviews.length}{" "}
                  ratings
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {additionalInfo && additionalInfo.additional_info && (
            <div className="p-4 sm:p-6 border-b border-[#F5E8EA]">
              <p className="text-[#232123] text-sm sm:text-base leading-relaxed">
                {additionalInfo.additional_info}
              </p>
            </div>
          )}

          {/* Get Directions */}
          <div className="mx-4 sm:mx-6 mt-4 mb-2">
            <div className="bg-[#f8f0ff] rounded-2xl p-4">
              <div className="w-full flex items-center justify-center gap-3 text-[#7b189f] font-medium text-sm sm:text-base">
                <div
                  onClick={() => {
                    window.open(businessDetail.url, "_blank");
                  }}
                  className="flex items-center cursor-pointer"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2"
                  >
                    <path
                      d="M22 12H18L15 21L9 3L6 12H2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Get Directions
                </div>
                {/*
                <span className="text-sm text-[#4f4d4f]">4 miles from you</span>
                */}
              </div>
            </div>
          </div>

          {/* Combined Address Details and Promotion */}
          <div className="mx-4 sm:mx-6 mt-4">
            {/* Container with proper border radius */}
            <div className="rounded-2xl overflow-hidden border border-[#F5E8EA]">
              {/* Address Details */}
              <div className="bg-white p-4 space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#f8f0ff] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-[#7b189f]" />
                  </div>
                  <div className="ml-3">
                    <p className="text-[#232123] text-[15px]">
                      {businessDetail.formatted_address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#f8f0ff] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="h-4 w-4 text-[#7b189f]" />
                  </div>
                  <div className="ml-3">
                    <p className="text-[#232123] text-[15px]">
                      {businessDetail &&
                      businessDetail.opening_hours &&
                      businessDetail.opening_hours.periods ? (
                        <>
                          <span className="font-semibold">
                            {businessDetail.opening_hours.open_now
                              ? "OPEN NOW"
                              : "CLOSED NOW"}
                          </span>
                          <span className="mx-1.5 text-[#4f4d4f]">·</span>
                          <span className="text-[#4f4d4f]">
                            {getClosingTimeText(businessDetail.opening_hours)}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#f8f0ff] rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-4 w-4 text-[#7b189f]" />
                  </div>
                  <div className="ml-3 flex items-center">
                    <span className="text-[#232123] text-[15px]">
                      $35 - $60
                    </span>
                    <span className="text-[#4f4d4f] text-[13px] ml-1.5">
                      per person
                    </span>
                  </div>
                </div>
              </div>

              {/* Promotion Banner */}
              {businessOffers && businessOffers.length > 0 && (
                <div className="bg-[#C6EB75] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      {/* description */}
                      <p className="text-[#2c3f02] text-[13px] mb-0.5">
                        {businessOffers[0].description}
                      </p>

                      <div className="flex items-center">
                        <span className="text-[#2c3f02] text-[15px] font-semibold mr-1">
                          ⚡
                        </span>
                        {/* title + discount percentage */}
                        <span className="text-[#2c3f02] text-[15px] font-semibold">
                          {businessOffers[0].title} - Flat{" "}
                          {businessOffers[0].discount_percentage}%
                        </span>
                      </div>
                    </div>

                    <button className="bg-[#2c3f02] text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                      Grab now
                      <span className="text-lg leading-none">→</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Menu */}
          <MenuSection businessCatalog={businessCatalog} />

          {/* Ratings & Reviews */}
          {averageRating && userReviews && (
            <RatingsAndReviews
              averageRating={averageRating}
              fetcher={setFetcher}
              userReviews={userReviews}
              businessDetail={businessDetail}
            />
          )}
        </div>
      </div>
    </div>
  );
}
